import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Label } from "@/components/ui/label";
import type { User } from "@/hooks/use-users";
import { useSetAccountPerson } from "@/hooks/use-users";

interface LinkPersonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
}

/**
 * The manual override for the account ↔ person link.
 *
 * Enable normally works this out itself by matching the work email when an
 * account is created or signs in, so reaching for this dialog means the emails
 * differ — or the account is shared and should stay unlinked.
 */
export function LinkPersonDialog({ open, onOpenChange, user }: LinkPersonDialogProps) {
  const [personId, setPersonId] = useState<string | null>(user.personId);
  const setAccountPerson = useSetAccountPerson();

  function handleSubmit() {
    if (personId === user.personId) {
      onOpenChange(false);
      return;
    }
    setAccountPerson.mutate(
      { userId: user.id, personId },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Linked person</DialogTitle>
          <DialogDescription>
            Which employee does {user.email} sign in as? This decides whose data they see on My
            Dashboard and My Certifications.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Person</Label>
          {/* `includeNone` is the unlink path — a shared or service account
              should have no employee behind it. */}
          <EntityCombobox
            entity="persons"
            value={personId}
            onChange={setPersonId}
            includeNone
            noneLabel="Not linked"
            placeholder="Search for a person…"
            searchPlaceholder="Search by name, title or email"
            className="w-full"
          />
          <p className="text-muted-foreground text-xs">
            Usually set automatically by matching the work email. A person can be linked to only one
            account.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={setAccountPerson.isPending}>
            {setAccountPerson.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
